import { SALESORDERSLINES } from 'constants/Entities';
import { getLiteral } from 'utils/getLiteral';
import { getDecimalsByConfig, DECIMAL_TYPES } from 'utils/decimal';

/**
 * Get decimals for product lines (from parent sales order context)
 * @param {string} type - DECIMAL_TYPES value
 * @param {string} fieldId - Field ID for maxDecimals lookup
 * @param {Object} salesOrderData - Parent SalesOrder data (optional)
 * @returns {number} Decimals to use
 */
const getLineDecimals = (type, fieldId, salesOrderData) => {
    let idEnvironment = salesOrderData?.idEnvironment || salesOrderData?.idBranch;
    let idCurrency = salesOrderData?.idCurrency;

    if (idEnvironment && typeof idEnvironment === 'object' && idEnvironment.value) {
        idEnvironment = idEnvironment.value;
    }

    if (idCurrency && typeof idCurrency === 'object' && idCurrency.value) {
        idCurrency = idCurrency.value;
    }

    return getDecimalsByConfig({
        type,
        idEnvironment,
        idCurrency,
        entityName: 'SALESORDERSLINES',
        fieldId,
    });
};

/**
 * Returns localeStringOptions for numberCell
 * @param {string} type - DECIMAL_TYPES value
 * @param {string} fieldId - Field ID for maxDecimals lookup
 * @param {Object} salesOrderData - Parent SalesOrder data (optional)
 * @returns {Object} { minimumFractionDigits, maximumFractionDigits }
 */
const getLocaleStringOptions = (type, fieldId, salesOrderData) => {
    const decimals = getLineDecimals(type, fieldId, salesOrderData);
    return {
        maximumFractionDigits: decimals,
        minimumFractionDigits: decimals,
    };
};

const getCalculatedReadOnlyInputParams = () => ({
    // Calculated monetary fields are presentation-only in the grid.
    // Source of truth is OrdersManager recalculation flow.
    isReadOnly: true,
    readOnly: true,
    isDisabled: true,
});

const getNumLinesWidth = (params) => {
    const totalPadding = 40;
    const checkbox = 26;
    const numberWidth = 9;
    if (params?.numLine?.isCrud && params?.data?.length) {
        const num =
            totalPadding + checkbox + Math.abs(params.data.length).toString().length * numberWidth;
        return num;
    } else if (params?.data?.length) {
        const num = totalPadding + Math.abs(params.data.length).toString().length * numberWidth;
        return num;
    } else return 100;
};

export default (params) => ({
    onlyFrontendCache: true,
    entity: SALESORDERSLINES,
    columnSelectorSections: [
        {
            label: '',
            fields: [
                'quantity',
                'price',
                'discount1',
                'discount2',
                'discount3',
                'discount4',
                ...(params?.useTaxesSalesOrders
                    ? ['taxRateId', 'subtotalAmount', 'lineDiscountTotalAmount', 'finalprice']
                    : ['finalprice']),
            ],
        },
    ],
    columnDefs: [
        {
            colId: 'numLine',
            headerName: '#',
            field: 'numLine',
            headerComponent: 'headerTextCell',
            headerComponentParams: {
                checkbox: {
                    onChange:
                        (!params?.disabledCheckboxes && params?.numLine?.onClickCheckbox) || null,
                },
            },
            cellRenderer: 'textCell',
            cellRendererParams: {
                checkbox: {
                    onChange:
                        (!params?.disabledCheckboxes && params?.numLine?.onClickCheckbox) || null,
                },
            },
            pinned: 'left',
            width: getNumLinesWidth(params),
            resizable: false,
            suppressSizeToFit: true,
            rowDrag: true,
        },
        {
            colId: 'description',
            headerName: getLiteral('label_product'),
            field: 'model',
            fieldConfiguration: 'description',
            cellRenderer: 'CarouselReadCell',
            cellRendererParams: {
                isSquared: true,
                imageSize: 'big',
                withEmptyPlaceholder: true,
                isReadOnly: params?.disabledCheckboxes,
                otherFields: {
                    rowImageSrc: 'rowImageUrl',
                    rowImagePlaceholder: 'rowImageUrlFallback',
                    secondaryTextKey: 'description',
                    carouselFieldId: 'imageListUrls',
                    carouselImageSrc: 'src',
                    carouselImagePlaceholder: 'fallbackSrc',
                },
            },
            pinned: 'left',
            width: 250,
            maxWidth: 1000,
            suppressSizeToFit: true,
            crudType: 'carousel',
        },
        {
            colId: 'quantity',
            headerName: getLiteral('title_quantity'),
            field: 'quantity',
            fieldConfiguration: 'quantity',
            cellRenderer: 'numberCell',
            crudType: 'decimal',
            suppressSizeToFit: true,
            width: 120,
            cellRendererParams: {
                align: 'right',
                localeStringOptions: getLocaleStringOptions(
                    DECIMAL_TYPES.QUANTITY,
                    'quantity',
                    params?.salesOrderData,
                ),
                inputParams: {
                    limitDecimals: getLineDecimals(
                        DECIMAL_TYPES.QUANTITY,
                        'quantity',
                        params?.salesOrderData,
                    ),
                },
            },
        },
        {
            colId: 'price',
            headerName: getLiteral('label_unitprice'),
            field: 'price',
            fieldConfiguration: 'price',
            cellRenderer: 'currencyCell',
            cellRendererParams: {
                align: 'right',
                otherFields: {
                    symbol: 'currencySymbol',
                    digits: getLineDecimals(
                        DECIMAL_TYPES.CURRENCY_PRICES,
                        'price',
                        params?.salesOrderData,
                    ),
                    longFormat: true,
                },
                inputParams: {
                    limitDecimals: getLineDecimals(
                        DECIMAL_TYPES.CURRENCY_PRICES,
                        'price',
                        params?.salesOrderData,
                    ),
                },
            },
            crudType: 'currency',
            suppressSizeToFit: true,
            width: 120,
        },
        {
            colId: 'discount1',
            headerName: `${getLiteral('label_salesorder_product_discount1')} (%)`,
            field: 'discount1',
            fieldConfiguration: 'discount1',
            cellRenderer: 'numberCell',
            cellRendererParams: {
                align: 'right',
                localeStringOptions: getLocaleStringOptions(
                    DECIMAL_TYPES.GENERAL,
                    'discount1',
                    params?.salesOrderData,
                ),
                inputParams: {
                    limitDecimals: getLineDecimals(
                        DECIMAL_TYPES.GENERAL,
                        'discount1',
                        params?.salesOrderData,
                    ),
                },
            },
            crudType: 'percentage',
            suppressSizeToFit: true,
            width: 90,
        },
        {
            colId: 'discount2',
            headerName: `${getLiteral('label_salesorder_product_discount2')} (%)`,
            field: 'discount2',
            fieldConfiguration: 'discount2',
            cellRenderer: 'numberCell',
            cellRendererParams: {
                align: 'right',
                localeStringOptions: getLocaleStringOptions(
                    DECIMAL_TYPES.GENERAL,
                    'discount2',
                    params?.salesOrderData,
                ),
                inputParams: {
                    limitDecimals: getLineDecimals(
                        DECIMAL_TYPES.GENERAL,
                        'discount2',
                        params?.salesOrderData,
                    ),
                },
            },
            crudType: 'percentage',
            suppressSizeToFit: true,
            width: 90,
            hide: true,
        },
        {
            colId: 'discount3',
            headerName: `${getLiteral('label_salesorder_product_discount3')} (%)`,
            field: 'discount3',
            fieldConfiguration: 'discount3',
            cellRenderer: 'numberCell',
            cellRendererParams: {
                align: 'right',
                localeStringOptions: getLocaleStringOptions(
                    DECIMAL_TYPES.GENERAL,
                    'discount3',
                    params?.salesOrderData,
                ),
                inputParams: {
                    limitDecimals: getLineDecimals(
                        DECIMAL_TYPES.GENERAL,
                        'discount3',
                        params?.salesOrderData,
                    ),
                },
            },
            crudType: 'percentage',
            suppressSizeToFit: true,
            width: 90,
            hide: true,
        },
        {
            colId: 'discount4',
            headerName: `${getLiteral('label_salesorder_product_discount4')} (%)`,
            field: 'discount4',
            fieldConfiguration: 'discount4',
            cellRenderer: 'numberCell',
            cellRendererParams: {
                align: 'right',
                localeStringOptions: getLocaleStringOptions(
                    DECIMAL_TYPES.GENERAL,
                    'discount4',
                    params?.salesOrderData,
                ),
                inputParams: {
                    limitDecimals: getLineDecimals(
                        DECIMAL_TYPES.GENERAL,
                        'discount4',
                        params?.salesOrderData,
                    ),
                },
            },
            crudType: 'percentage',
            suppressSizeToFit: true,
            width: 90,
            hide: true,
        },
        ...(params?.useTaxesSalesOrders
            ? [
                  // CRUD: syncStatusEnabled / schema readOnly / lines locked → textCell (plain text)
                  // instead of read-only Select; otherwise Select in ProductLinesTable.
                  {
                      colId: 'taxRateId',
                      headerName: getLiteral('label_products_default_tax'),
                      field: 'taxRateId',
                      cellRenderer: 'textCell',
                      crudType: 'singleValueList',
                      suppressSizeToFit: true,
                      width: 130,
                  },
                  {
                      colId: 'subtotalAmount',
                      headerName: getLiteral('label_subtotal'),
                      field: 'subtotalAmount',
                      cellRenderer: 'currencyCell',
                      cellRendererParams: {
                          align: 'right',
                          otherFields: {
                              symbol: 'currencySymbol',
                              digits: getLineDecimals(
                                  DECIMAL_TYPES.CURRENCY_PRICES,
                                  'subtotalAmount',
                                  params?.salesOrderData,
                              ),
                              longFormat: true,
                          },
                          inputParams: {
                              ...getCalculatedReadOnlyInputParams(),
                          },
                      },
                      suppressSizeToFit: true,
                      width: 130,
                  },
                  {
                      colId: 'lineDiscountTotalAmount',
                      headerName: getLiteral('label_salesorder_line_total_discounts'),
                      field: 'lineDiscountTotalAmount',
                      cellRenderer: 'currencyCell',
                      cellRendererParams: {
                          align: 'right',
                          otherFields: {
                              symbol: 'currencySymbol',
                              digits: getLineDecimals(
                                  DECIMAL_TYPES.CURRENCY_PRICES,
                                  'subtotalAmount',
                                  params?.salesOrderData,
                              ),
                              longFormat: true,
                          },
                          inputParams: {
                              ...getCalculatedReadOnlyInputParams(),
                          },
                      },
                      suppressSizeToFit: true,
                      width: 130,
                  },
                  {
                      colId: 'finalprice',
                      headerName: getLiteral('label_total'),
                      field: 'finalprice',
                      cellRenderer: 'currencyCell',
                      cellRendererParams: {
                          align: 'right',
                          otherFields: {
                              symbol: 'currencySymbol',
                              digits: getLineDecimals(
                                  DECIMAL_TYPES.CURRENCY_PRICES,
                                  'finalPrice',
                                  params?.salesOrderData,
                              ),
                              longFormat: true,
                          },
                          inputParams: {
                              ...getCalculatedReadOnlyInputParams(),
                              limitDecimals: getLineDecimals(
                                  DECIMAL_TYPES.CURRENCY_PRICES,
                                  'finalPrice',
                                  params?.salesOrderData,
                              ),
                          },
                      },
                      suppressSizeToFit: true,
                      width: 130,
                  },
              ]
            : [
                  {
                      colId: 'finalprice',
                      headerName: getLiteral('label_total'),
                      field: 'finalprice',
                      cellRenderer: 'currencyCell',
                      cellRendererParams: {
                          align: 'right',
                          otherFields: {
                              symbol: 'currencySymbol',
                              digits: getLineDecimals(
                                  DECIMAL_TYPES.CURRENCY_PRICES,
                                  'finalPrice',
                                  params?.salesOrderData,
                              ),
                              longFormat: true,
                          },
                          inputParams: {
                              ...getCalculatedReadOnlyInputParams(),
                              limitDecimals: getLineDecimals(
                                  DECIMAL_TYPES.CURRENCY_PRICES,
                                  'finalPrice',
                                  params?.salesOrderData,
                              ),
                          },
                      },
                      suppressSizeToFit: true,
                  },
              ]),
    ],
});
